import PushNotifications from './components/PushNotifications';
import AppearanceDialog from './components/AppearanceDialog';
import QueuePreview from './components/QueuePreview';
import InstallAppPrompt from './components/InstallAppPrompt';
import {preferredFilter} from './components/FavoriteStar';
import BackgroundPicker from './components/BackgroundPicker';
import {PremiumContext,usePremiumAccount,PremiumPanel,PremiumChoices} from './components/Premium';
import {SwipeLobby} from './components/SwipeDiscovery';
import { track, analyticsId } from './analytics';
import {AnalyticsActivity} from './components/AnalyticsPreferences';
import { callSeconds } from './callTiming';
import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import './App.css';
import './CallExperience.css';
import './ConnectionMotion.css';
import './Discovery.css';
import './Welcome.css';
import DiscoveryLobby from './components/DiscoveryLobby';
import { ConnectionLoader, StageTransition } from './components/ConnectionMotion';
import { CallDock, CallIcon, StageProgress } from './components/CallUI';
import { UserContext } from './components/UserContext';
import SignIn from './components/SignIn';
import SignUp from './components/SignUp';
import Profile from './components/Profile';
import Avatar from './components/Avatar';
import resolveImageUrl from './components/profileImageUrl';
import Chat from './components/Chat';
import SignOut from './components/SignOut';
import ConversationPrompt from './components/ConversationPrompt';
import {attachCallRecovery} from './components/callRecovery';
import SharedIcebreaker from './components/SharedIcebreaker';
import PremiumGiftPopup from './components/PremiumGiftPopup';
import VoiceAtmosphere, { VoiceSoundwave } from './components/VoiceAtmosphere';
import { createProtectedStream } from './components/protectedStream';
import { DEFAULT_FILTER, clearCustomFilters, registerCustomFilter } from './components/FaceFiltersRegistry';
import FilterWorkshop from './components/FilterWorkshop';
import MatchesPage from './components/MatchesPage';
import MatchChatRoom from './components/MatchChatRoom';
import ContactUs from './components/ContactUs';
import ReportModal from './components/ReportModal';
import FilterPicker from './components/FilterPicker';
import MatchCallControls from './components/MatchCallControls';
import {createVoiceEffects,VOICE_EFFECTS,isPremiumVoice} from './components/voiceEffects';
import NotificationCenter from './components/NotificationCenter';
import FloatingError from './components/FloatingError';
import DateVenuePhoto from './components/DateVenuePhoto';
import SocialAuth from './components/SocialAuth';
import DatePlanner from './components/DatePlanner';
import API_BASE_URL from './config';

const debugLog = (...args) => {
  if (process.env.NODE_ENV === 'development') console.log(...args);
};

// ICE server config: Google STUN + optional TURN from env vars
// Defined at module level so it is a stable reference (no useCallback dependency issues)
const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    ...(process.env.REACT_APP_TURN_URL ? [{
      urls: process.env.REACT_APP_TURN_URL,
      username: process.env.REACT_APP_TURN_USERNAME || '',
      credential: process.env.REACT_APP_TURN_CREDENTIAL || '',
    }] : []),
  ],
};

// Safe wrapper around getUserMedia that shows a clear error on non-HTTPS origins
const safeGetUserMedia = async (constraints) => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    const isHttps = window.location.protocol === 'https:';
    const msg = isHttps
      ? "הגישה למצלמה או למיקרופון אינה זמינה בדפדפן הזה."
      : 'כדי להשתמש במצלמה ובמיקרופון, יש לפתוח את האפליקציה בחיבור HTTPS מאובטח.';
    alert(msg);
    throw new Error('getUserMedia not available');
  }
  return navigator.mediaDevices.getUserMedia(constraints);
};

const getJwtPayload = (jwtToken) => {
  try {
    const payload = jwtToken.split('.')[1];
    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(normalizedPayload));
  } catch (error) {
    return null;
  }
};

// Flow stages:
// 0 - Auth screen
// 1 - Lobby (match button)
// 2 - In queue (waiting counter)
// 3 - Match found! 30s preparation
// 4 - Audio call (1 minute, turn-based 30s each)
// 5 - Video call with filters (3 min counter)
// 6 - Filters off + Set Date button
// 7 - Completed

function App() {
  const { currentUser, setCurrentUser, token, setToken } = useContext(UserContext);
  const premium=usePremiumAccount(token);
  const [premiumGiftVisible,setPremiumGiftVisible]=useState(false);
  const [showPremium,setShowPremium]=useState(false),[showPremiumChoices,setShowPremiumChoices]=useState(false);
  const [showWorkshop,setShowWorkshop]=useState(false);
  const [filterLibraryVersion,setFilterLibraryVersion]=useState(0);
  const [filterLoadError,setFilterLoadError]=useState('');
  const [filterLoadAttempt,setFilterLoadAttempt]=useState(0);
  useEffect(()=>{
    let active=true;
    clearCustomFilters();setFilterType(DEFAULT_FILTER);setShowWorkshop(false);setFilterLoadError('');
    if(currentUser?.id && token) axios.get(`${API_BASE_URL}/custom_filters`,{headers:{Authorization:`Bearer ${token}`}})
      .then(({data})=>{if(active){data.filters.forEach(registerCustomFilter);setFilterLibraryVersion(v=>v+1);}})
      .catch(()=>{if(active)setFilterLoadError("לא ניתן לטעון את הפילטרים השמורים.");});
    return ()=>{active=false;clearCustomFilters();};
  },[currentUser?.id,token,filterLoadAttempt]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStage, setCurrentStage] = useState(0);
  const currentStageRef = useRef(currentStage);
  currentStageRef.current = currentStage;
  const [socket, setSocket] = useState(null);
  const [searchExpanded,setSearchExpanded]=useState(false);
  const [expandDismissed,setExpandDismissed]=useState(false);
  const [expandPending,setExpandPending]=useState(false);
  const [expandError,setExpandError]=useState('');
  const [queueTime, setQueueTime] = useState(0);
  const [audioCallTimeLeft, setAudioCallTimeLeft] = useState(60);
  const [videoCallTimeLeft, setVideoCallTimeLeft] = useState(180);
  const [icebreaker,setIcebreaker]=useState('');
  const [audioAdvanceVoters,setAudioAdvanceVoters]=useState([]);
  const [questionRound,setQuestionRound]=useState(0),[questionVoters,setQuestionVoters]=useState([]);
  const questionRoundRef=useRef(0);
  const [firstSpeakerId, setFirstSpeakerId] = useState(null);
  const [partnerId, setPartnerId] = useState(null);
  const [partnerUsername, setPartnerUsername] = useState('');
  const [partnerAge, setPartnerAge] = useState(null);
  const [partnerImage, setPartnerImage] = useState('');
  const [isChatVisible, setIsChatVisible] = useState(false);
  const [remoteStream, setRemoteStream] = useState(null);
  const [remoteVolume, setRemoteVolume] = useState(1.0);
  const [authMode, setAuthMode] = useState(null);
  const [callError, setCallError] = useState('');
  const [partnerReconnecting,setPartnerReconnecting]=useState(false);
  const [readyWaiting, setReadyWaiting] = useState(false);
  const [revealVoters,setRevealVoters]=useState([]);
  const [revealWaiting, setRevealWaiting] = useState(false);
  const [continueWaiting, setContinueWaiting] = useState(false);
  const [socketReady, setSocketReady] = useState(false);
  const [queueMessage, setQueueMessage] = useState('');
  const mediaGeneration = useRef(0);
  const localMedia = useRef(null);
  const voiceMedia = useRef(null);
  const voiceEffectRef = useRef('natural');
  const [voiceEffect,setVoiceEffect]=useState('natural');
  const [voiceError,setVoiceError]=useState('');
  const [panelsHidden,setPanelsHidden]=useState(true);
  const [fullscreen,setFullscreen]=useState(false);
  const videoStageRef=useRef(null);
  const callMenuRef=useRef(null);
  useEffect(()=>{const update=()=>setFullscreen(Boolean(document.fullscreenElement));document.addEventListener('fullscreenchange',update);return()=>document.removeEventListener('fullscreenchange',update);},[]);
  useEffect(()=>{
    if(currentStage!==5&&currentStage!==6){
      setPanelsHidden(true);setIsVideoPanelMinimized(true);
      if(document.fullscreenElement?.classList.contains('App'))document.exitFullscreen().catch(()=>{});
    }
  },[currentStage]);
  const prepareVoice=useCallback(async source=>{
    let processor;
    try {
      processor=createVoiceEffects(source,{onEffectChange:name=>{voiceEffectRef.current=name;setVoiceEffect(name);}});
      voiceMedia.current=processor;
      await processor.setEffect(voiceEffectRef.current);
      setVoiceError('');return processor.stream;
    }catch{
      // Keep the microphone usable if Web Audio is unsupported or suspended.
      processor?.stop({keepSource:true});
      if(voiceMedia.current===processor)voiceMedia.current=null;
      voiceEffectRef.current='natural';setVoiceEffect('natural');
      setVoiceError("האפקטים הקוליים אינם זמינים. המיקרופון פועל בקול הטבעי שלכם.");return source;
    }
  },[]);
  const changeVoice=async value=>{
    if(isPremiumVoice(value)&&!premium.active){setShowPremium(true);return;}
    voiceEffectRef.current=value;setVoiceEffect(value);
    try{if(!voiceMedia.current){setVoiceEffect('natural');voiceEffectRef.current='natural';setVoiceError("האפקטים הקוליים אינם זמינים בשיחה הזאת.");return;}await voiceMedia.current.setEffect(value);setVoiceError('');}
    catch{setVoiceError("האפקטים הקוליים מושהים. בחרו קול מחדש כדי להמשיך.");}
  };
  const protectedMedia = useRef(null);
  const revealAllowed = useRef(false);
  const maskRef = useRef(DEFAULT_FILTER);
  const [background,setBackground]=useState('none'),[backgroundStatus,setBackgroundStatus]=useState('');
  const backgroundRef=useRef('none');
  const changeBackground=id=>{backgroundRef.current=id;setBackground(id);setBackgroundStatus('');};
  const sessionRef = useRef(null);
  const partnerRef = useRef(null);
  const stopMedia = useCallback(() => {
    setPartnerReconnecting(false);
    mediaGeneration.current += 1;
    protectedMedia.current?.stop();
    protectedMedia.current = null;
    voiceMedia.current?.stop();voiceMedia.current=null;
    localMedia.current?.getTracks().forEach(track => track.stop());
    localMedia.current = null;
    peerConnection.current?.recovery?.stop();
    peerConnection.current?.close();
    peerConnection.current = null;
  }, []);
  useEffect(() => stopMedia, [stopMedia]);
  const localVideoRef = useRef();   // used for video element in stage 5/6
  const localAudioRef = useRef();   // used for audio element in stage 4
  const remoteVideoRef = useRef();
  const peerConnection = useRef(null);
  const [, setIsFilterEnabled] = useState(true);
  const [localStream, setLocalStream] = useState(null);
  const [confirmedDate, setConfirmedDate] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const queueTimerRef = useRef(null);
  const queueStartedAt = useRef(null);
  const analyticsSavedSession = useRef(null);
  const pendingIceCandidatesRef = useRef([]);
  const pendingOffer = useRef(null);
  const matchCallGeneration = useRef(0);
  const [filterType, setFilterType] = useState(DEFAULT_FILTER);
  const [micMuted, setMicMuted] = useState(false);
  const micMutedRef = useRef(false);
  useEffect(() => { micMutedRef.current = micMuted; }, [micMuted]);
  const [filterStatus, setFilterStatus] = useState('loading');
  const [isVideoPanelMinimized, setIsVideoPanelMinimized] = useState(true);
  useEffect(() => { sessionRef.current = sessionId; partnerRef.current = partnerId; }, [sessionId, partnerId]);
  useEffect(() => { maskRef.current = filterType; }, [filterType]);

  // Matches page & chat room state
  const [showMatchesPage, setShowMatchesPage] = useState(false);
  const [showContactUs, setShowContactUs] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [activeChatMatch, setActiveChatMatch] = useState(null); // the match object currently open in chat
  const activeMatchRef = useRef(null);
  useEffect(() => { activeMatchRef.current = activeChatMatch; }, [activeChatMatch]);
  // Match room call state (video/phone call initiated from match chat room)
  const [matchRoomCallType, setMatchRoomCallType] = useState(null); // 'video' | 'phone'
  // matchRoomPartnerId tracks the partner for the active match-room call
  const matchRoomPartnerIdRef = useRef(null);
  const matchRoomLocalVideoRef = useRef();
  const matchRoomRemoteVideoRef = useRef();
  const matchRoomPeerConnection = useRef(null);
  const [matchRoomRemoteStream, setMatchRoomRemoteStream] = useState(null);
  const [showMatchRoomCall, setShowMatchRoomCall] = useState(false);
  const [matchFiltersEnabled,setMatchFiltersEnabled]=useState(false);
  const matchFiltersRef=useRef(false);
  const queueFilterPreset=useRef(null);
  useEffect(()=>{
    if(currentStage===0)queueFilterPreset.current=null;
    if(currentStage===2){const selected=preferredFilter(currentUser?.id);queueFilterPreset.current=selected;maskRef.current=selected;setFilterType(selected);}
    if(currentStage===5||showMatchRoomCall){const selected=showMatchRoomCall?preferredFilter(currentUser?.id):(queueFilterPreset.current||preferredFilter(currentUser?.id));maskRef.current=selected;setFilterType(selected);}
  },[currentStage,showMatchRoomCall,currentUser?.id]);
  const changeMatchFilter=id=>{maskRef.current=id;setFilterType(id);matchFiltersRef.current=true;setMatchFiltersEnabled(true);};
  const disableMatchFilters=()=>{matchFiltersRef.current=false;setMatchFiltersEnabled(false);};
  useEffect(()=>{if(showMatchRoomCall)localMedia.current?.getAudioTracks().forEach(track=>{track.enabled=!micMuted;});},[micMuted,showMatchRoomCall]);
  useEffect(()=>{if(matchRoomRemoteVideoRef.current)matchRoomRemoteVideoRef.current.volume=remoteVolume;},[remoteVolume,showMatchRoomCall,matchRoomRemoteStream]);

  const expireSession=useCallback(()=>{
    stopMedia();setSocketReady(false);setIsLoading(false);
    setShowMatchesPage(false);setActiveChatMatch(null);setShowMatchRoomCall(false);setIsChatVisible(false);setShowReportModal(false);
    sessionRef.current=null;partnerRef.current=null;matchRoomPartnerIdRef.current=null;
    setSessionId(null);setPartnerId(null);setAuthMode('signin');setCurrentStage(0);
    localStorage.removeItem('token');localStorage.removeItem('currentUser');localStorage.removeItem('profileImageUrl');
    setToken(null);setCurrentUser(null);
  },[stopMedia,setToken,setCurrentUser]);
  const authRef=useRef({token,expireSession});
  authRef.current={token,expireSession,userId:currentUser?.id};
  const socketUserId = currentUser?.id;
  // Connect to socket on mount
  useEffect(() => {
    const newSocket = io(API_BASE_URL);
    setSocket(newSocket);

    newSocket.on('connect', () => {
      debugLog('Connected to socket server');
      // Register session when we connect (pass JWT token for auth)
      if (authRef.current.userId) {
        const tok = authRef.current.token || localStorage.getItem('token');
        newSocket.emit('register_session', { user_id: authRef.current.userId, token: tok, resume_call: Boolean(peerConnection.current) });
      }
    });

    newSocket.on('session_registered', () => { peerConnection.current?.recovery?.recover();setSocketReady(true); setCallError(value => value === "מתחברים מחדש לשרת… השיחה נשארת פתוחה." ? '' : value); });
    newSocket.on('account_deleted', () => authRef.current.expireSession());
    newSocket.on('session_error', data => { if(data.code==='auth_required'||["יש להיכנס לחשבון מחדש.","נדרשת כניסה לחשבון."].includes(data.message)){authRef.current.expireSession();return;}setCallError(data.message);setIsLoading(false); });
    newSocket.on('disconnect', () => {
      setSocketReady(false);
      // A signaling interruption does not imply the peer media connection ended.
      setIsLoading(false);
      if (peerConnection.current) setCallError("מתחברים מחדש לשרת… השיחה נשארת פתוחה.");
    });

    const resume=()=>{
      if(document.visibilityState==='hidden'||!authRef.current.userId||!authRef.current.token)return;
      if(!newSocket.connected){newSocket.connect();return;}
      newSocket.emit('register_session',{user_id:authRef.current.userId,token:authRef.current.token,resume_call:Boolean(sessionRef.current||matchRoomPartnerIdRef.current||peerConnection.current)});
    };
    document.addEventListener('visibilitychange',resume);window.addEventListener('online',resume);
    const heartbeat=setInterval(()=>{
      if(newSocket.connected&&authRef.current.userId)newSocket.emit('session_heartbeat',{});
    },30000);
    return () => {
      clearInterval(heartbeat);
      document.removeEventListener('visibilitychange',resume);window.removeEventListener('online',resume);
      newSocket.off('connect');
      newSocket.off('disconnect');
      newSocket.close();
    };
  }, [stopMedia]);

  // Reuse the already-warmed transport on login. Disconnect on logout so the
  // previous account's server-side socket identity cannot survive sign-out.
  const registeredAccount=useRef(null);
  useEffect(()=>{
    if(!socket)return;
    if(!socketUserId||!token){
      if(registeredAccount.current){socket.disconnect();setSocketReady(false);registeredAccount.current=null;}
      return;
    }
    registeredAccount.current=socketUserId;
    if(!socket.connected){socket.connect();return;}
    socket.emit('register_session',{user_id:socketUserId,token,resume_call:Boolean(peerConnection.current)});
  },[socket,socketUserId,token]);

  const createPeerConnection = useCallback((partnerIdForCall, mediaKind = 'audio') => {
    if (peerConnection.current) {
      peerConnection.current.recovery?.stop();
      peerConnection.current.close();
    }
    const pc = new RTCPeerConnection(ICE_SERVERS);
    peerConnection.current = pc;
    pc.mediaKind = mediaKind;
    pc.analyticsCallId=analyticsId();pc.analyticsStarted=Date.now();
    track('call_started',{call_id:pc.analyticsCallId,kind:mediaKind});

    debugLog('PC CREATED', 'signaling=', pc.signalingState, 'ice=', pc.iceConnectionState, 'gathering=', pc.iceGatheringState, 'connection=', pc.connectionState);

    pc.onicecandidate = (event) => {
      if (!event.candidate) {
        debugLog('ICE gathering finished');
        return;
      }
      debugLog('LOCAL ICE:', event.candidate.type, event.candidate.protocol, event.candidate.address);
      if (socket) {
        socket.emit('ice_candidate', { candidate: event.candidate, partner_id: String(partnerIdForCall) });
      }
    };

    pc.onicecandidateerror = (event) => {
      console.error('ICE candidate error:', event.errorCode, event.errorText, event.url);
    };

    pc.onicegatheringstatechange = () => {
      debugLog('ICE gathering state:', pc.iceGatheringState);
    };

    pc.recovery=attachCallRecovery(pc,{
      isOfferer:Number(socketUserId)<Number(partnerIdForCall),isCurrent:()=>peerConnection.current===pc,
      isOnline:()=>Boolean(socket?.connected),onStatus:message=>{
        if(peerConnection.current!==pc)return;
        if(message&&!pc.recoveryAnnounced){track('call_recovering',{call_id:pc.analyticsCallId,kind:pc.mediaKind});pc.recoveryAnnounced=true;}
        if(message&&socket?.connected&&(!pc.lastRecoveryNotice||Date.now()-pc.lastRecoveryNotice>=8000)){pc.lastRecoveryNotice=Date.now();socket.emit('recover_call',{partner_id:String(partnerIdForCall)});}
        if(!message){if(!pc.everConnected)track('call_connected',{call_id:pc.analyticsCallId,kind:pc.mediaKind,duration_ms:Math.min(86400000,Date.now()-pc.analyticsStarted)});if(pc.recoveryAnnounced)track('call_recovered',{call_id:pc.analyticsCallId,kind:pc.mediaKind});pc.recoveryAnnounced=false;pc.lastRecoveryNotice=0;pc.everConnected=true;setPartnerReconnecting(false);socket?.emit('recover_call',{partner_id:String(partnerIdForCall),connected:true});}
        setCallError(previous=>!message&& !previous.startsWith("מתחברים מחדש")?previous:message);
      },
      onExhausted:()=>{
        if(peerConnection.current!==pc)return;
        track('call_failed',{call_id:pc.analyticsCallId,kind:pc.mediaKind});
        const matchedPartner=matchRoomPartnerIdRef.current;
        if(matchedPartner){
          socket?.emit('match_call_end',{to_user_id:String(matchedPartner)});
          matchCallGeneration.current+=1;matchRoomPartnerIdRef.current=null;
          setShowMatchRoomCall(false);setMatchRoomRemoteStream(null);
        }else if(sessionRef.current){
          socket?.emit('end_conversation',{session_id:sessionRef.current});
          sessionRef.current=null;partnerRef.current=null;
          setSessionId(null);setPartnerId(null);setCurrentStage(1);
        }
        stopMedia();setPartnerReconnecting(false);
        setCallError("לא הצלחנו לחבר את השיחה מחדש. נסו שוב.");
      },
      sendOffer:offer=>socket.emit(pc.mediaKind==='video'?'video_chat_offer':'audio_call_offer',{offer,partner_id:String(partnerIdForCall),room:String(partnerIdForCall)})
    });
    pc.ontrack = (event) => {
      debugLog('Remote track received:', event.track.kind, 'readyState:', event.track.readyState, 'muted:', event.track.muted);
      event.track.onunmute = () => debugLog('Remote track UNMUTED:', event.track.kind);
      event.track.onmute = () => debugLog('Remote track MUTED:', event.track.kind);
      if (matchRoomPartnerIdRef.current) {
        setMatchRoomRemoteStream(event.streams[0]);
        return;
      }
      setRemoteStream(event.streams[0]);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
    };

    return pc;
  }, [socket,socketUserId,stopMedia]);

  const restoreMatchCall=useRef(null);
  const restoringSession=useRef(null);
  // Listen for match events
  useEffect(() => {
    if (!socket) return;

    const restore=async data=>{
      if(restoringSession.current===data.session_id)return;
      const stage={matched:3,audio_call:4,video_call:5,filters_off:6,date_planning:8}[data.status];
      if(!stage)return;
      // Support the previous server response while frontend/backend roll out.
      const remaining = callSeconds(data.remaining ?? data['נותרו'], stage === 4 ? 60 : 180);
      const round = callSeconds(data.question_round);
      setIcebreaker(data.icebreaker || "איזה דבר קטן תמיד מעלה לכם חיוך?");
      questionRoundRef.current=round;setQuestionRound(round);
      setQuestionVoters(Array.isArray(data.voters) ? data.voters : []);
      setRevealVoters(data.reveal_voters || []);
      setAudioAdvanceVoters(Array.isArray(data.audio_advance_voters)?data.audio_advance_voters:[]);
      if(sessionRef.current===data.session_id&&peerConnection.current){
        if(stage===4)setAudioCallTimeLeft(remaining);
        if(stage===5)setVideoCallTimeLeft(remaining);
        revealAllowed.current=stage>=6;setCurrentStage(stage);
        peerConnection.current.recovery?.recover(true);return;
      }
      restoringSession.current=data.session_id;
      sessionRef.current=data.session_id;partnerRef.current=data.partner_id;
      const generation=mediaGeneration.current;
      try{
        if(stage===4||stage===8){
          const stream=await safeGetUserMedia({audio:true});
          if(generation!==mediaGeneration.current){stream.getTracks().forEach(t=>t.stop());return;}
          stream.getAudioTracks().forEach(t=>{t.enabled=!micMutedRef.current;});localMedia.current=stream;setLocalStream(stream);
        }
        sessionRef.current=data.session_id;partnerRef.current=data.partner_id;
        setSessionId(data.session_id);setPartnerId(data.partner_id);setPartnerUsername(data.partner_username);
        revealAllowed.current=stage>=6;setIsLoading(false);setReadyWaiting(false);setRevealWaiting(false);setContinueWaiting(false);
        setAudioCallTimeLeft(remaining);setVideoCallTimeLeft(remaining);setCurrentStage(stage);
      }catch{setCallError("אפשרו גישה למיקרופון וחזרו לאפליקציה כדי להתחבר מחדש.");}
      finally{restoringSession.current=null;}
    };
    socket.on('conversation_restored',restore);
    socket.on('match_call_restored',data=>{if(!matchRoomPartnerIdRef.current){setActiveChatMatch({partner_id:data.partner_id,partner_username:data.partner_username});restoreMatchCall.current?.(data.partner_id,data.call_type);}});
    socket.on('partner_connection_lost',data=>{if(Number(data.from_user_id)===Number(matchRoomPartnerIdRef.current||partnerRef.current))setPartnerReconnecting(true);});
    socket.on('peer_reconnected',data=>{const pc=peerConnection.current;if(Number(data.from_user_id)===Number(matchRoomPartnerIdRef.current||partnerRef.current)&&(pc?.connectionState==='connected'||['connected','completed'].includes(pc?.iceConnectionState)))setPartnerReconnecting(false);});
    socket.on('peer_reconnecting',data=>{if(Number(data.from_user_id)===Number(matchRoomPartnerIdRef.current||partnerRef.current)){if(peerConnection.current?.everConnected)setPartnerReconnecting(true);peerConnection.current?.recovery?.recover(true);}});
    socket.on('search_expanded', data=>{setSearchExpanded(true);setExpandPending(false);setExpandError('');setQueueMessage(data.message);});
    socket.on('search_expansion_error', data=>{setExpandPending(false);setExpandError(data.message);});
    socket.on('queue_joined', (data) => {
      setSearchExpanded(false);setExpandDismissed(false);setExpandPending(false);setExpandError('');
      queueStartedAt.current=Date.now();track('queue_joined');
      debugLog('Queue joined:', data);
      setCurrentStage(2);
      // Start queue timer
      queueTimerRef.current = setInterval(() => {
        setQueueTime(prev => prev + 1);
      }, 1000);
    });

    socket.on('queue_status', (data) => {
      setQueueMessage(data.message === "ממתינים להתאמה…" ? "עדיין אין התאמה זמינה. אפשר להמתין כאן או לחזור בהמשך." : data.message);
    });

    socket.on('queue_error', (data) => {
      console.error('Queue error:', data);
      alert(data.message);
      setCurrentStage(1);
      setIsLoading(false);
    });

    socket.on('match_found', (data) => {
      track('match_found',queueStartedAt.current?{duration_ms:Math.min(86400000,Date.now()-queueStartedAt.current)}:{});queueStartedAt.current=null;
      debugLog('Match found!', data);
      setIsLoading(false);
      // Stop queue timer
      if (queueTimerRef.current) {
        clearInterval(queueTimerRef.current);
        queueTimerRef.current = null;
      }
      setQueueTime(0);
      
      // Set session info
      revealAllowed.current = false;
      setReadyWaiting(false);
      setRevealWaiting(false);
      setContinueWaiting(false);
      setCallError('');
      setSessionId(data.session_id);
      setPartnerId(data.partner_id);
      setPartnerUsername(data.partner_username);
      setPartnerAge(data.partner_age);
      setPartnerImage(resolveImageUrl(data.partner_image));
      setFirstSpeakerId(data.first_speaker_id);
      
      // Go to preparation stage
      setCurrentStage(3);
    });

    socket.on('audio_advance_votes', data=>{if(data.session_id===sessionRef.current)setAudioAdvanceVoters(data.voters || []);});
    socket.on('audio_call_started', (data) => {
      setAudioAdvanceVoters([]);
      debugLog('Audio call started:', data);
      setIcebreaker(data.icebreaker || "איזה דבר קטן תמיד מעלה לכם חיוך?");
      questionRoundRef.current=data.question_round||0;setQuestionRound(questionRoundRef.current);setQuestionVoters([]);
      setAudioCallTimeLeft(60);
      setCurrentStage(4);
    });

    socket.on('icebreaker_votes',data=>{
      if(data.session_id===sessionRef.current&&data.question_round===questionRoundRef.current)setQuestionVoters(data.voters);
    });
    socket.on('icebreaker_changed',data=>{
      if(data.session_id!==sessionRef.current||data.question_round<=questionRoundRef.current)return;
      questionRoundRef.current=data.question_round;setQuestionRound(data.question_round);setQuestionVoters([]);setIcebreaker(data.icebreaker);
    });
    socket.on('video_call_started', (data) => {
      setRevealVoters([]);setRevealWaiting(false);
      debugLog('Video call started:', data);
      setIsFilterEnabled(true);
      setVideoCallTimeLeft(callSeconds(data.duration,180));
      setCurrentStage(5);
    });

    socket.on('filters_removed', (data) => {
      debugLog('Filters removed:', data);
      if (data.session_id !== sessionRef.current) return;
      revealAllowed.current = true;
      setIsFilterEnabled(false);
      setCurrentStage(6);
    });

    const onEnded = data => {
      if (data.session_id === activeMatchRef.current?.session_id) {
        stopMedia();
        matchCallGeneration.current += 1;
        matchRoomPartnerIdRef.current = null;
        setShowMatchRoomCall(false);
        setActiveChatMatch(null);
      }
      if (data.session_id !== sessionRef.current) return;
      stopMedia();
      sessionRef.current=null;partnerRef.current=null;
      setPartnerReconnecting(false);
      setRemoteStream(null);
      setSessionId(null);
      setPartnerId(null);
      setIsLoading(false);setReadyWaiting(false);setRevealWaiting(false);setContinueWaiting(false);
      setCurrentStage(1);
      setCallError("השיחה הסתיימה. אפשר להכיר מישהו חדש כשמתאים לכם.");
    };
    const onSaved = data => {
      if (data.session_id !== sessionRef.current) return;
      if(data.date)setConfirmedDate(data.date);
      if(analyticsSavedSession.current!==data.session_id){track('mutual_match');analyticsSavedSession.current=data.session_id;}
      stopMedia();
      setCurrentStage(7);
    };
    socket.on('conversation_ended', onEnded);
    socket.on('connection_saved', onSaved);
    const onPlanning = data => {
      if (data.session_id !== sessionRef.current) return;
      // Keep the existing audio connection and the user's mute choice.
      protectedMedia.current?.stop({ keepAudio: true });
      protectedMedia.current = null;
      localMedia.current?.getVideoTracks?.().forEach(track => track.stop());
      if(peerConnection.current)peerConnection.current.mediaKind='audio';
      setCurrentStage(8);
    };
    socket.on('date_planning_started', onPlanning);
    socket.on('ready_waiting', () => setReadyWaiting(true));
    socket.on('reveal_votes', data => {if(data.session_id!==sessionRef.current)return;setRevealVoters(data.voters || []);setRevealWaiting((data.voters || []).includes(currentUser.id));});
    socket.on('reveal_waiting', data => {if(data.session_id===sessionRef.current)setRevealWaiting(true);});
    socket.on('continue_waiting', () => setContinueWaiting(true));
    socket.on('date_proposed', (data) => {
      alert(data.message);
    });

    const answerOffer = async (data, kind) => {
      const expected = matchRoomPartnerIdRef.current || partnerRef.current;
      if (Number(data.from_user_id) !== Number(expected)) return;
      if (kind === 'audio' && !matchRoomPartnerIdRef.current && [5,6].includes(currentStageRef.current)) return;
      let pc = peerConnection.current;
      if (kind === 'audio' && !matchRoomPartnerIdRef.current && localMedia.current && !pc) {
        pc = createPeerConnection(expected);
        localMedia.current.getTracks().forEach(track => pc.addTrack(track, localMedia.current));
      }
      if (!pc || pc.mediaKind !== kind) { pendingOffer.current = { data, kind }; return; }
      try {
        await pc.setRemoteDescription(data.offer);
        await flushPendingIceCandidates(pc);
        await pc.setLocalDescription(await pc.createAnswer());
        socket.emit(kind === 'video' ? 'video_chat_answer' : 'audio_call_answer', {
          answer: pc.localDescription, room: String(expected), partner_id: String(expected),
        });
      } catch (error) { setCallError("לא הצלחנו להתחיל את השיחה. סיימו אותה ונסו שוב."); }
    };
    socket.on('video_chat_offer', data => answerOffer(data, 'video'));
    socket.on('audio_call_offer', data => answerOffer(data, 'audio'));
    const applyAnswer = async (data, kind) => {
      const expected = matchRoomPartnerIdRef.current || partnerRef.current;
      if (Number(data.from_user_id) !== Number(expected)) return;
      const pc = peerConnection.current;
      if (!pc || pc.mediaKind !== kind || pc.signalingState !== 'have-local-offer') return;
      try {
        await pc.setRemoteDescription(data.answer);
        await flushPendingIceCandidates(pc);
      } catch (error) { setCallError("לא הצלחנו לחבר את השיחה. נסו שוב."); }
    };
    socket.on('video_chat_answer', data => applyAnswer(data, 'video'));
    socket.on('audio_call_answer', data => applyAnswer(data, 'audio'));
    socket.on('ice_candidate', async data => {
      const expected = matchRoomPartnerIdRef.current || partnerRef.current;
      if (Number(data.from_user_id) !== Number(expected)) return;
      const pc = peerConnection.current;
      if (!pc?.remoteDescription) { pendingIceCandidatesRef.current.push(data.candidate); return; }
      try { await pc.addIceCandidate(data.candidate); } catch (error) {
        // Late candidates from the previous audio connection or ICE generation
        // can be rejected during video setup. Connection-state recovery handles
        // genuine transport failures; one obsolete candidate is not a failed call.
        debugLog('ICE candidate rejected during negotiation:', error);
      }
    });
    socket.on('match_call_ended', data => {
      if (Number(data.from_user_id) !== Number(matchRoomPartnerIdRef.current)) return;
      stopMedia();
      matchCallGeneration.current += 1;
      matchRoomPartnerIdRef.current = null;
      setShowMatchRoomCall(false);
      setMatchRoomRemoteStream(null);
    });

    socket.on('queue_left', (data) => {
      debugLog('Left queue:', data);
      setCurrentStage(1);
    });

    return () => {
      socket.off('conversation_restored',restore);socket.off('match_call_restored');socket.off('peer_reconnecting');socket.off('partner_connection_lost');socket.off('peer_reconnected');
      socket.off('conversation_ended', onEnded);
      socket.off('connection_saved', onSaved);
      socket.off('date_planning_started', onPlanning);
      socket.off('ready_waiting');
      socket.off('reveal_waiting');
      socket.off('reveal_votes');
      socket.off('continue_waiting');
      socket.off('search_expanded');socket.off('search_expansion_error');
      socket.off('queue_joined');
      socket.off('queue_status');
      socket.off('queue_error');
      socket.off('match_found');
      socket.off('audio_call_started');
      socket.off('audio_advance_votes');
      socket.off('icebreaker_votes');socket.off('icebreaker_changed');
      socket.off('audio_turn_swapped');
      socket.off('video_call_started');
      socket.off('filters_removed');
      socket.off('date_proposed');
      socket.off('video_chat_offer');
      socket.off('video_chat_answer');
      socket.off('audio_call_offer');
      socket.off('audio_call_answer');
      socket.off('ice_candidate');
      socket.off('queue_left');
      socket.off('match_call_ended');
    };
  }, [socket, currentUser, createPeerConnection, stopMedia]);

  // Audio call timer (stage 4) - 60 seconds total
  useEffect(() => {
    if (currentStage === 4 && audioCallTimeLeft > 0) {
      const interval = setInterval(() => {
        setAudioCallTimeLeft(prev => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [currentStage, audioCallTimeLeft, socket, sessionId, firstSpeakerId]);

  // Video call timer (stage 5) - 3 minutes
  useEffect(() => {
    if (currentStage === 5 && videoCallTimeLeft > 0) {
      const interval = setInterval(() => {
        setVideoCallTimeLeft(prev => {
          if (prev <= 1) {
            // The timer unlocks the reveal choice; consent is explicit.
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [currentStage, videoCallTimeLeft, socket, sessionId]);


  // Initial auth check
  useEffect(() => {
    stopMedia();
    if (currentUser && token) {
      setCurrentStage(1);
    } else {
      setCurrentStage(0);setIsLoading(false);setSocketReady(false);setShowMatchesPage(false);setActiveChatMatch(null);setShowMatchRoomCall(false);setIsChatVisible(false);setShowReportModal(false);
      sessionRef.current=null;partnerRef.current=null;matchRoomPartnerIdRef.current=null;setSessionId(null);setPartnerId(null);
      pendingOffer.current=null;pendingIceCandidatesRef.current=[];
      if(queueTimerRef.current){clearInterval(queueTimerRef.current);queueTimerRef.current=null;}
    }
  }, [currentUser, token, stopMedia]);

  const flushPendingIceCandidates = async (pc) => {
    while (pendingIceCandidatesRef.current.length > 0) {
      const candidate = pendingIceCandidatesRef.current.shift();
      try {
        await pc.addIceCandidate(candidate);
      } catch (err) {
        console.error('Error adding queued ICE candidate:', err);
      }
    }
  };


  const toggleMute = () => setMicMuted(value => !value);

  const renderVoiceControls = () => <><label className="voice-effect-control">קול<select aria-label="אפקט קולי" value={voiceEffect} onChange={e=>changeVoice(e.target.value)}>{VOICE_EFFECTS.map(([id,label])=><option key={id} value={id} disabled={isPremiumVoice(id)&&!premium.active}>{label}{isPremiumVoice(id)?(premium.active?" · Premium":" · 🔒 למנויי Premium בלבד"):""}</option>)}</select></label>{voiceError&&<span role="alert">{voiceError}</span>}</>;
  const renderBackgrounds=()=> <BackgroundPicker modal value={background} onChange={changeBackground} status={backgroundStatus}/>;
  const renderCallDock = (children) => (
    <CallDock muted={micMuted} onMute={toggleMute}
      volume={remoteVolume} onVolume={setRemoteVolume} onEnd={handleReturnToLobby}
      onReport={() => setShowReportModal(true)}>{renderVoiceControls()}{[5,6].includes(currentStage)?<div className="appearance-picker-row">{children}{renderBackgrounds()}</div>:children}</CallDock>
  );

  const handleFindMatch = (selectedUserId=null,choiceToken=null,automatic=false) => {
    if(!Number.isInteger(selectedUserId))selectedUserId=null;
    if(premium.match_choices_active&&!selectedUserId&&!automatic){setShowPremiumChoices(true);return;}
    setShowPremiumChoices(false);
    setIsLoading(true);
    if (socket?.connected && socketReady) {
      setCallError('');
      setQueueMessage('');
      
      const tok = token || localStorage.getItem('token');
      socket.emit('join_match_queue', { user_id: currentUser.id, token: tok, ...(selectedUserId ? {selected_user_id:selectedUserId,choice_token:choiceToken} : {}) });
    } else {
      alert("אין חיבור לשרת");
      setIsLoading(false);
    }
  };

  const handleLeaveQueue = () => {
    track('queue_cancelled');queueStartedAt.current=null;
    if (socket?.connected) {
      socket.emit('leave_match_queue', { user_id: currentUser.id });
    }
    if (queueTimerRef.current) {
      clearInterval(queueTimerRef.current);
      queueTimerRef.current = null;
    }
    setQueueTime(0);
    setIsLoading(false);
    setCurrentStage(1);
  };

  const handleReadyForCall = async () => {
    if (readyWaiting || !socket?.connected) return;
    setCallError('');
    const attempt = mediaGeneration.current;
    try {
      let stream = await safeGetUserMedia({ audio: true });
      if (attempt !== mediaGeneration.current) { stream.getTracks().forEach(track => track.stop()); return; }
      stream=await prepareVoice(stream);
      if(attempt!==mediaGeneration.current){stream.getTracks().forEach(track=>track.stop());return;}
      localMedia.current = stream;
      stream.getAudioTracks().forEach(track => { track.enabled = false; });
      setReadyWaiting(true);
      socket.emit('start_audio_call', { session_id: sessionId });
    } catch (error) { setCallError("לא ניתן לגשת למיקרופון. בדקו את ההרשאות ונסו שוב."); }
  };

  useEffect(() => {
    if (![4,8].includes(currentStage) || !localMedia.current || peerConnection.current) return;
    const pc = createPeerConnection(partnerId);
    const stream = localMedia.current;
    stream.getTracks().forEach(track => pc.addTrack(track, stream));
    (async () => {
      try {
        const pending = pendingOffer.current;
        if (pending?.kind === 'audio' && Number(pending.data.from_user_id) === Number(partnerId)) {
          pendingOffer.current = null;
          await pc.setRemoteDescription(pending.data.offer);
          await flushPendingIceCandidates(pc);
          await pc.setLocalDescription(await pc.createAnswer());
          socket.emit('audio_call_answer', { answer: pc.localDescription, partner_id: String(partnerId), room: String(partnerId) });
          return;
        }
        if(Number(currentUser?.id)>Number(partnerId))return;
        await pc.setLocalDescription(await pc.createOffer());
        socket.emit('audio_call_offer', { offer: pc.localDescription, partner_id: String(partnerId) });
      } catch (error) { setCallError("החיבור הקולי נכשל. סיימו את השיחה ונסו שוב."); }
    })();
  }, [currentStage, currentUser?.id, partnerId, createPeerConnection, socket]);

  useEffect(() => {
    localMedia.current?.getAudioTracks().forEach(track => {
      track.enabled = !micMuted;
    });
  }, [currentStage, micMuted, localStream]);

  const startVideoCall = useCallback(async () => {
    if (!partnerId || !socket) return;
    const amIOfferer = Number(currentUser?.id) < Number(partnerId);
    try {
      stopMedia();
      const attempt = mediaGeneration.current;
      let raw = await safeGetUserMedia({ video: true, audio: true });
      if (attempt !== mediaGeneration.current) { raw.getTracks().forEach(track => track.stop()); return; }
      raw=await prepareVoice(raw);
      if(attempt!==mediaGeneration.current){raw.getTracks().forEach(track=>track.stop());return;}
      raw.getAudioTracks().forEach(track => { track.enabled = !micMutedRef.current; });
      localMedia.current = raw;
      protectedMedia.current = createProtectedStream(raw, () => revealAllowed.current, () => maskRef.current, setFilterStatus, {getBackground:()=>backgroundRef.current,onBackgroundStatus:setBackgroundStatus});
      const stream = protectedMedia.current.stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      setLocalStream(stream);
      // BOTH users create PC and add local tracks
      const pc = createPeerConnection(partnerId, 'video');
      stream.getTracks().forEach((track) => {
        if (pc.signalingState !== 'closed') {
          pc.addTrack(track, stream);
        }
      });
      const pending = pendingOffer.current;
      pendingOffer.current = null;
      if (pending?.kind === 'video' && Number(pending.data.from_user_id) === Number(partnerId)) {
          await pc.setRemoteDescription(pending.data.offer);
          await flushPendingIceCandidates(pc);
          await pc.setLocalDescription(await pc.createAnswer());
          socket.emit('video_chat_answer', { answer: pc.localDescription, room: String(partnerId) });
        return;
      }
      // Only the offerer creates the SDP offer
      if (!amIOfferer) return;
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socket.emit('video_chat_offer', {
        offer: pc.localDescription,
        from_user_id: currentUser.id,
        room: String(partnerId)
      });
    } catch (error) {
      stopMedia();
      setCallError(error.message || "לא ניתן להפעיל וידאו עם הגנת פרטיות. סיימו את השיחה ונסו שוב.");
    }
    }, [createPeerConnection, socket, partnerId, currentUser, stopMedia, prepareVoice]);

  // Start video call when entering stage 5
  useEffect(() => {
    if (currentStage === 5 || (currentStage === 6 && !protectedMedia.current)) {
      startVideoCall();
    }
  }, [currentStage, startVideoCall]);

  useEffect(() => {
    const el = remoteVideoRef.current;
    if (el && remoteStream) {
      // Only set srcObject if it changed (prevents aborting an in-progress play)
      if (el.srcObject !== remoteStream) {
        el.srcObject = remoteStream;
      }
      // Always try to play — browser may block autoplay for unmuted audio
      el.play().then(() => {
        debugLog('Remote media play started successfully');
      }).catch(err => {
        console.error('Remote play error:', err.name, err.message);
        // If NotAllowedError, schedule a retry after a short delay
        if (err.name === 'NotAllowedError') {
          setTimeout(() => {
            if (el.paused) el.play().catch(() => {});
          }, 500);
        }
      });
    }
  }, [remoteStream, currentStage]);

  useEffect(() => {
    if (localVideoRef.current && protectedMedia.current) localVideoRef.current.srcObject = protectedMedia.current.stream;
  }, [currentStage]);

  const updateProfile = async (profileData) => {
    try {
      const activeToken = token || localStorage.getItem('token');
      if (!activeToken) {
        alert("פרטי ההתחברות חסרים. היכנסו מחדש.");
        return;
      }

      const tokenPayload = getJwtPayload(activeToken);
      if (!tokenPayload || typeof tokenPayload.sub !== 'string') {
        localStorage.removeItem('token');
        localStorage.removeItem('currentUser');
        setToken(null);
        setCurrentUser(null);
        alert("פרטי ההתחברות פגו. היכנסו מחדש ועדכנו את הפרופיל.");
        return;
      }

      const response = await axios.post(`${API_BASE_URL}/update_profile`, profileData, {
        headers: { Authorization: `Bearer ${activeToken}` }
      });
      setCurrentUser(response.data.updatedUser);
      localStorage.setItem('currentUser', JSON.stringify(response.data.updatedUser));
    } catch (error) {
      if (error.response && error.response.data) {
        const serverMessage = error.response.data.message || error.response.data.msg || "עדכון הפרופיל נכשל.";
        console.error('Profile update failed:', error.response.status, error.response.data);

        if (error.response.status === 422 && serverMessage.includes('Subject must be a string')) {
          localStorage.removeItem('token');
          localStorage.removeItem('currentUser');
          setToken(null);
          setCurrentUser(null);
          alert("פרטי ההתחברות פגו. היכנסו מחדש ועדכנו את הפרופיל.");
          return;
        }

        alert(serverMessage);
      } else {
        console.error('Profile update failed:', error);
        alert("עדכון הפרופיל נכשל. נסו שוב.");
      }
    }
  };

  // ── Match room call handlers ──────────────────────────────────────────────
  const startMatchRoomCall = useCallback(async (partnerIdForCall, kind) => {
    stopMedia();
    const attempt = ++matchCallGeneration.current;
    const mediaAttempt = mediaGeneration.current;
    matchRoomPartnerIdRef.current = partnerIdForCall;
    matchFiltersRef.current=false;setMatchFiltersEnabled(false);
    setMatchRoomCallType(kind);
    setShowMatchRoomCall(true);
    try {
      let stream = await safeGetUserMedia({ audio: true, video: kind === 'video' });
      if (attempt !== matchCallGeneration.current || mediaAttempt !== mediaGeneration.current) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      stream = await prepareVoice(stream);
      if (attempt !== matchCallGeneration.current || mediaAttempt !== mediaGeneration.current) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      localMedia.current = stream;
      stream.getAudioTracks().forEach(track=>{track.enabled=!micMutedRef.current;});
      let outgoing=stream;
      if(kind==='video'){
        protectedMedia.current=createProtectedStream(stream,()=>!matchFiltersRef.current,()=>maskRef.current,setFilterStatus,{getBackground:()=>backgroundRef.current,onBackgroundStatus:setBackgroundStatus});
        outgoing=protectedMedia.current.stream;
      }
      if (matchRoomLocalVideoRef.current) matchRoomLocalVideoRef.current.srcObject = outgoing;
      const pc = createPeerConnection(partnerIdForCall, kind==='video'?'video':'audio');
      matchRoomPeerConnection.current = pc;
      outgoing.getTracks().forEach(track => pc.addTrack(track, outgoing));
      const pending = pendingOffer.current;
      pendingOffer.current = null;
      if (pending?.kind === pc.mediaKind && Number(pending.data.from_user_id) === Number(partnerIdForCall)) {
        await pc.setRemoteDescription(pending.data.offer);
        await flushPendingIceCandidates(pc);
        await pc.setLocalDescription(await pc.createAnswer());
        socket.emit(kind === 'video' ? 'video_chat_answer' : 'audio_call_answer', {
          answer: pc.localDescription, room: String(partnerIdForCall), partner_id: String(partnerIdForCall),
        });
      } else if (Number(currentUser.id) < Number(partnerIdForCall)) {
        await pc.setLocalDescription(await pc.createOffer());
        socket.emit(kind === 'video' ? 'video_chat_offer' : 'audio_call_offer', {
          offer: pc.localDescription, room: String(partnerIdForCall), partner_id: String(partnerIdForCall),
        });
      }
    } catch (error) {
      stopMedia();
      setCallError("לא ניתן להתחיל את השיחה. בדקו את הרשאות המכשיר ונסו שוב.");
      setShowMatchRoomCall(false);
      socket?.emit('match_call_end', { to_user_id: partnerIdForCall });
      matchRoomPartnerIdRef.current = null;
    }
  }, [socket, stopMedia, createPeerConnection, currentUser, prepareVoice]);
  restoreMatchCall.current=startMatchRoomCall;
  const startMatchRoomVideoCall = useCallback(partner => startMatchRoomCall(partner, 'video'), [startMatchRoomCall]);
  const startMatchRoomPhoneCall = useCallback(partner => startMatchRoomCall(partner, 'phone'), [startMatchRoomCall]);

  const endMatchRoomCall = () => {
    const partner = matchRoomPartnerIdRef.current;
    if (partner) socket?.emit('match_call_end', { to_user_id: partner });
    matchCallGeneration.current += 1;
    stopMedia();
    pendingOffer.current = null;
    pendingIceCandidatesRef.current = [];
    matchRoomPeerConnection.current = null;
    matchRoomPartnerIdRef.current = null;
    setMatchRoomRemoteStream(null);
    setShowMatchRoomCall(false);
    setMatchRoomCallType(null);
  };

  // Sync match room remote stream to video element
  useEffect(() => {
    if (matchRoomRemoteVideoRef.current && matchRoomRemoteStream) {
      matchRoomRemoteVideoRef.current.srcObject = matchRoomRemoteStream;
    }
  }, [matchRoomRemoteStream]);

  const handleReturnToLobby = () => {
    setShowPremiumChoices(false);
    setConfirmedDate(null);
    setIsLoading(false);setReadyWaiting(false);setRevealWaiting(false);setContinueWaiting(false);
    setQueueTime(0);setQueueMessage('');
    if(queueTimerRef.current){clearInterval(queueTimerRef.current);queueTimerRef.current=null;}
    if (sessionId) socket?.emit('end_conversation', { session_id: sessionId });
    stopMedia();
    sessionRef.current=null;partnerRef.current=null;
    revealAllowed.current = false;
    pendingOffer.current = null;
    pendingIceCandidatesRef.current = [];
    setCallError('');
    // Clean up
    if (peerConnection.current) {
      peerConnection.current.recovery?.stop();
      peerConnection.current.close();
      peerConnection.current = null;
    }
    setSessionId(null);
    setPartnerId(null);
    setPartnerUsername('');
    setPartnerAge(null);
    setFirstSpeakerId(null);
    setIcebreaker('');
    setRemoteStream(null);
    setLocalStream(null);
    setCurrentStage(1);
  };

  const formatTime = (value) => {
    const seconds = callSeconds(value);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Sync remote volume to the audio element
  useEffect(() => {
    if (remoteVideoRef.current) {
      remoteVideoRef.current.volume = remoteVolume;
    }
  }, [remoteVolume, currentStage]);

  // ============ RENDER METHODS ============

  const renderAuthScreen = () => (
    <div className="case-zero welcome-entry" dir="rtl" style={{'--auth-garden-image': `url("${process.env.PUBLIC_URL}/backgrounds/auth-garden.png")`}}>
      <div className="auth-forms">
        <section className="welcome-hero">
          <h1 className="app-head"><img className="app-brand-logo" src={`${process.env.PUBLIC_URL}/connext_logo.png`} alt="Connext" /></h1>
          <span className="welcome-kicker">ספיד דייטינג · מכירים אחרת</span>
          <h2>היכרות שמתחילה<br /><em>בשיחה אמיתית.</em></h2>
          <p>שיחה קצרה, וידאו עם פילטרים, ואולי הדייט הבא שלכם.<br />ממשיכים לשלב הבא רק כששניכם רוצים.</p>
          <ol className="welcome-path" aria-label="איך מכירים ב-Connext">
            <li><span aria-hidden="true">01</span>שיחה קולית</li>
            <li><span aria-hidden="true">02</span>וידאו עם פילטרים</li>
            <li><span aria-hidden="true">03</span>חשיפה הדדית</li>
            <li><span aria-hidden="true">04</span>קובעים דייט</li>
          </ol>
        </section>
      </div>
      <div className="welcome-auth-actions">
        <div className="auth-choice" role="group" aria-label="אפשרויות חשבון">
          <button aria-pressed={authMode === 'signup'} onClick={() => setAuthMode('signup')}>יצירת חשבון</button>
          <button aria-pressed={authMode === 'signin'} onClick={() => setAuthMode('signin')}>כניסה</button>
        </div>
        {!authMode && <SocialAuth dividerText="או ממשיכים עם" />}
        {authMode === 'signup' && <SignUp initiallyOpen onClose={() => setAuthMode(null)} />}
        {authMode === 'signin' && <SignIn initiallyOpen onClose={() => setAuthMode(null)} />}
      </div>
      <div style={{ paddingBottom: '20px', textAlign: 'center' }}>
        <button
          onClick={() => setShowContactUs(true)}
          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}
        >
          יצירת קשר
        </button>
        <nav aria-label="מידע משפטי" style={{display:"flex",justifyContent:"center",gap:16,marginTop:12,fontSize:13}}><a href="/privacy.html" style={{color:"#b9cee5"}}>מדיניות פרטיות</a><a href="/terms.html" style={{color:"#b9cee5"}}>תנאי שימוש</a></nav>
      </div>
    </div>
  );

  const renderLobby = () => (
    <SwipeLobby token={token} onMatches={()=>setShowMatchesPage(true)}>
    <div className="case-one">
      <div className="users-lobby">
        <DiscoveryLobby username={currentUser?.username} loading={isLoading} connected={socketReady}
          onDiscover={() => handleFindMatch()} onMatches={() => setShowMatchesPage(true)} onWorkshop={()=>setShowWorkshop(true)} />
        <button className="premium-lobby-button" onClick={()=>setShowPremium(true)}>✦ {premium.active?'Premium פעיל':'Connext Premium · $18 לחודש'}</button>
        {showWorkshop && <div className="lobby-workshop"><FilterWorkshop onClose={()=>setShowWorkshop(false)} onSave={id=>{setFilterType(id);setShowWorkshop(false);setFilterLibraryVersion(v=>v+1);}} /></div>}
        {filterLoadError && <p role="status">{filterLoadError} <button onClick={()=>setFilterLoadAttempt(v=>v+1)}>טעינת הפילטרים השמורים מחדש</button></p>}
        <div style={{ paddingBottom: '20px', textAlign: 'center' }}>
          <button
            onClick={() => setShowContactUs(true)}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}
          >
            יצירת קשר
          </button>
        </div>
      </div>
    </div>
    </SwipeLobby>
  );

  const renderQueue = () => (
    <div className="case-two">
      <div className="queue-container">
        <ConnectionLoader title="ההתרגשות כבר כאן." detail="מחפשים את השיחה הבאה שלכם…" />
        <p role="status">{queueMessage}</p>
        <div className="queue-timer">
          <div className="timer-display">{formatTime(queueTime)}</div>
          <p>זמן המתנה</p>
        </div>
        {queueTime>=45 && !premium.match_choices_active && !searchExpanded && !expandDismissed && <section className="queue-expand-prompt" aria-label="הרחבת החיפוש">
          <h3>רוצים להרחיב את החיפוש?</h3><p>אפשר לחפש התאמה בכל הארץ, מעבר ל־50 ק״מ. העדפות הגיל והמגדר שלכם נשארות כפי שהגדרתם.</p>
          {expandError&&<p role="alert">{expandError}</p>}
          <button disabled={expandPending||!socket?.connected} onClick={()=>{setExpandPending(true);setExpandError('');socket.emit('expand_match_search',{});}}>{expandPending?'מרחיבים את החיפוש…':'כן, לחפש בכל הארץ'}</button>
          <button disabled={expandPending} onClick={()=>setExpandDismissed(true)}>להמשיך בקרבת מקום</button>
        </section>}
        {(searchExpanded||premium.match_choices_active)&&<p role="status">{premium.match_choices_active?"Premium · חיפוש בכל הארץ, בהתאם לטווח החיפוש של הצד השני":"החיפוש הורחב לכל הארץ"}</p>}
        <QueuePreview filter={filterType} background={background} onFilter={id=>{queueFilterPreset.current=id;maskRef.current=id;setFilterType(id);}} onBackground={changeBackground}/>
        <ConversationPrompt />
        <button className="cancel-button" onClick={handleLeaveQueue}>
          ביטול
        </button>
      </div>
    </div>
  );

  const renderPreparation = () => (
    <div className="case-three">
      <div className="preparation-container">
        <div className="match-info">
          <div className="match-anonymous-avatar">
            <Avatar src={partnerImage} alt={`${partnerUsername} — תמונת פרופיל`} placeholder={partnerUsername.slice(0, 1).toUpperCase()} />
          </div>
          <h2>מצאנו התאמה!</h2>
          <p className="partner-info-text">
            יש לכם התאמה עם <strong>{partnerUsername}</strong>, גיל {partnerAge}
          </p>
          <p className="preparation-subtitle">
            קחו נשימה. השיחה מתחילה כששניכם מוכנים.
          </p>
          <button className="ready-button" disabled={readyWaiting} onClick={handleReadyForCall}>
            {readyWaiting ? "ממתינים לצד השני…" : "אפשר להתחיל"}
          </button>
          <button className="lobby-button" onClick={handleReturnToLobby}>יציאה מהשיחה</button>
        </div>
      </div>
    </div>
  );

  const renderAudioCall = () => {
    return (
      <div className="case-four">
        <div className="audio-call-container">
          <VoiceAtmosphere />
          <div className="audio-visualizer">
            <div className="mic-icon"><CallIcon name={!micMuted ? 'mic' : 'muted'} /></div>
            <h2>שוברים את הקרח ביחד</h2>
            <p className="turn-hint">שני המיקרופונים פתוחים. מתחילים בשאלה ונותנים לשיחה לזרום.</p>
          </div>
          <div className="audio-timer">
            <VoiceSoundwave />
            <div className="audio-timer-ring">
              <svg width="160" height="160">
                <circle cx="80" cy="80" r="72" fill="none" stroke="#333" strokeWidth="6" />
                <circle 
                  cx="80" cy="80" r="72" fill="none" 
                  stroke="#4ecdc4" 
                  strokeWidth="6"
                  strokeDasharray={`${2 * Math.PI * 72}`}
                  strokeDashoffset={`${2 * Math.PI * 72 * (1 - audioCallTimeLeft / 60)}`}
                  transform="rotate(-90 80 80)"
                  style={{ transition: 'stroke-dashoffset 1s linear' }}
                />
              </svg>
              <div className="audio-timer-text">
                <div className="time">{formatTime(audioCallTimeLeft)}</div>
                <div className="time-label">נותרו</div>
              </div>
            </div>
            <VoiceSoundwave />
          </div>
          {audioCallTimeLeft === 0 && <section className="audio-advance-prompt" aria-label="מעבר לווידאו">
            <p>ממשיכים להכיר? השלב הבא הוא וידאו עם פילטרים לפנים. הסרת הפילטרים דורשת הסכמה של שניכם.</p>
            <button type="button" className="btn-2" disabled={!socket?.connected || audioAdvanceVoters.includes(currentUser.id)} onClick={()=>socket.emit('vote_audio_advance',{session_id:sessionId})}>
              {audioAdvanceVoters.includes(currentUser.id)?'הצבעתם · ממתינים לצד השני':'אני רוצה לעבור לשלב הבא'}
            </button>
            <p role="status">{audioAdvanceVoters.length}/2 הצביעו למעבר · אפשר להמשיך לדבר בינתיים</p>
          </section>}
          <SharedIcebreaker question={icebreaker} round={questionRound} voters={questionVoters} userId={currentUser.id} onVote={()=>{track('question_vote');socket?.emit('vote_icebreaker',{session_id:sessionId,question_round:questionRound});}}/>
          <div className="audio-call-stage-info"><p>דקה להכיר קצת יותר</p><p>{micMuted?"המיקרופון שלכם מושתק.":"המיקרופון שלכם פעיל."}</p></div>
          {renderCallDock()}
        </div>
        <audio ref={localAudioRef} autoPlay muted />
        <audio ref={remoteVideoRef} autoPlay />
      </div>
    );
  };

  const renderVideoCall = () => {
    const filtersPhase = !revealAllowed.current;

    return (
      <div className="case-five">
        <div className="video-chat-stage">

          <div className="call-session-heading">
            <div><h2>קצת מסתורין.</h2><p>תנו לשיחה לזרום. נחשפים כששניכם מוכנים.</p></div>

          </div>
          {/* ── Full-screen video grid ── */}
          <div className="video-grid">
            <div className="video-wrapper">
              <video className="video-con local-video" ref={localVideoRef} autoPlay playsInline muted></video>

              <div className="video-label">אתם</div>
            </div>
            <div className="video-wrapper">
              <video className="video-con" ref={remoteVideoRef} autoPlay playsInline></video>
              <div className="video-label">{partnerUsername || "הצד השני"}</div>
            </div>
          </div>

          {videoCallTimeLeft === 0 && <div className="call-decision reveal-decision">
            {revealVoters.length>0 && <p className="reveal-vote-notice" role="status">{revealVoters.some(id=>id!==currentUser.id)?"הצד השני מוכן להיחשף. הבחירה עכשיו שלכם":"הבקשה שלכם נשלחה לצד השני"} · {revealVoters.length}/2</p>}
            <button type="button" disabled={revealWaiting || revealVoters.includes(currentUser.id)} onClick={() => { track('reveal_requested');setRevealWaiting(true); socket.emit('end_video_call_phase', { session_id: sessionId }); }}><span className="decision-icon"><CallIcon name={(revealWaiting || revealVoters.includes(currentUser.id)) ? "check" : "sparkle"} /></span><span>{(revealWaiting || revealVoters.includes(currentUser.id)) ? "ממתינים לצד השני…" : "אפשר להיחשף"}</span></button>
          </div>}

                {filtersPhase && <p className="call-status" role="status">
                  {filterStatus === 'loading' && "טוענים זיהוי פנים…"}
                  {filterStatus === 'camera' && "ממתינים למצלמה…"}
                  {filterStatus === 'camera-ended' && "המצלמה התנתקה. עדיין אפשר לדבר. סיימו את השיחה ובדקו את המצלמה לפני שתצטרפו שוב."}
                  {filterStatus === 'playback' && <>תצוגת המצלמה מושהית. <button onClick={() => protectedMedia.current?.retry()}>הפעלת המצלמה מחדש</button></>}
                  {filterStatus === 'slow' && "זיהוי הפנים מתעכב. השיחה הקולית נמשכת והווידאו יחזור כשהזיהוי יתחדש."}
                  {filterStatus === 'no-face' && "הווידאו מטושטש כשהפנים מחוץ לתמונה. הפילטר יחזור כשהפנים יזוהו שוב."}
                  {filterStatus === 'error' && <>זיהוי הפנים אינו זמין. <button onClick={() => protectedMedia.current?.retry()}>ניסיון נוסף לזיהוי פנים</button></>}
                </p>}

        </div>
      </div>
    );
  };

  const renderFiltersOff = () => (
    <div className="case-six">
      <div className="filters-off-container">

        <div className="call-session-heading"><div><h2>נעים להכיר.</h2><p>שניכם בחרתם להיחשף. ממשיכים להכיר.</p></div><span className="session-timer">נחשפתם</span></div>
        {/* Full-screen video grid (reuses same refs) */}
        <div className="video-grid">
          <div className="video-wrapper">
            <video className="video-con local-video" ref={localVideoRef} autoPlay playsInline muted></video>
            <div className="video-label">אתם</div>
          </div>
          <div className="video-wrapper">
            <video className="video-con" ref={remoteVideoRef} autoPlay playsInline></video>
            <div className="video-label">{partnerUsername || "הצד השני"}</div>
          </div>
        </div>

        <div className="call-decision reveal-decision">
          <button type="button" disabled={continueWaiting} onClick={() => { setContinueWaiting(true); socket.emit('keep_talking', { session_id: sessionId }); }}>
            <span className="decision-icon"><CallIcon name={continueWaiting ? "check" : "message"} /></span><span>{continueWaiting ? "ממתינים לצד השני…" : "אשמח להמשיך לדבר"}</span>
          </button>
        </div>



      </div>
    </div>
  );

  const completeDatePlanning = useCallback((date) => {
    if(date)setConfirmedDate(date);
    stopMedia();
    setCurrentStage(7);
  }, [stopMedia]);

  const renderDatePlanning = () => (
    <div className="live-date-stage">
      <header><span className="live-date-badge"><CallIcon name={micMuted ? 'muted' : 'mic'} />{micMuted ? "המיקרופון מושתק" : "השיחה הקולית ממשיכה"}</span>
        <h2>יש חיבור. קובעים דייט.</h2><p>אתם וגם {partnerUsername} יכולים להמשיך לדבר בזמן שבוחרים.</p></header>
      <DatePlanner key={sessionId} partnerId={partnerId} userId={currentUser.id} token={token} socket={socket} live callControls={renderCallDock()} onCompleted={completeDatePlanning} />
      <audio ref={remoteVideoRef} autoPlay />
    </div>
  );

  const renderCompleted = () => (
    <div className="case-seven date-poster-completion">
      <img className="date-completion-art" src={`${process.env.PUBLIC_URL}/backgrounds/date-confirmed.png`} alt="Connext – מכירים אחרת" />
      <div className="completed-container">
        <div className="completed-icon" aria-hidden="true">✦</div>
        <p className="confirmation-congratulations">מזל טוב, יש לכם דייט!</p>

        {confirmedDate && <section className="confirmed-date-summary" aria-label="פרטי הדייט שנקבע">
          <DateVenuePhoto plan={confirmedDate} partnerId={partnerId} token={token} />
          <span>המקום שבחרתם</span><strong>{confirmedDate.location}</strong>
          <span>התאריך והשעה</span><time dateTime={confirmedDate.time}>{new Date(confirmedDate.time).toLocaleString('he-IL', {dateStyle:'full',timeStyle:'short'})}</time>
        </section>}
        <p className="completed-text">
          הדייט נקבע. החיבור שלכם עם {partnerUsername} נשמר כעת בהתאמות שלי.
        </p>
        <button className="discovery-primary" onClick={handleReturnToLobby}>חזרה ללובי <span aria-hidden="true">↗</span></button>
        <button className="lobby-button" onClick={() => setShowMatchesPage(true)}>
          להתאמות שלי
        </button>
      </div>
    </div>
  );

  const renderCurrentStage = () => {
    switch (currentStage) {
      case 0: return renderAuthScreen();
      case 1: return renderLobby();
      case 2: return renderQueue();
      case 3: return renderPreparation();
      case 4: return renderAudioCall();
      case 5: return renderVideoCall();
      case 6: return renderFiltersOff();
      case 7: return renderCompleted();
      case 8: return renderDatePlanning();
      default: return renderLobby();
    }
  };

  const openNotificationChat = async (partner, username) => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/get_matches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const match = (data.matches || []).find(item => Number(item.partner_id) === Number(partner));
      if (!match) {
        setCallError("החיבור הזה אינו זמין יותר.");
        return;
      }
      setShowMatchesPage(true);
      setActiveChatMatch(match);
    } catch {
      setCallError(`לא ניתן לפתוח את השיחה עם ${username || "ההתאמה שלכם"}. נסו שוב.`);
    }
  };

  return (
    <PremiumContext.Provider value={premium}>
    <div className="App">
      {currentUser && <PremiumGiftPopup key={currentUser.id} userId={currentUser.id} eligible={Boolean(premium.promotion_active && currentStage === 1 && !showMatchesPage && !showMatchRoomCall && !showWorkshop && !showPremium)} onVisibilityChange={setPremiumGiftVisible} />}
      <InstallAppPrompt eligible={Boolean(!premiumGiftVisible && currentUser && token && currentStage === 1 && !showMatchesPage && !showMatchRoomCall && !showWorkshop && !showPremium)} />
      {showPremium&&<PremiumPanel onClose={()=>setShowPremium(false)}/>}
      {showPremiumChoices&&<PremiumChoices token={token} onClose={()=>setShowPremiumChoices(false)} onChoose={handleFindMatch} onAuto={()=>handleFindMatch(null,null,true)}/>}
      <AnalyticsActivity />
      {currentUser&&partnerReconnecting&&<aside className="partner-reconnecting-notice" role="status" aria-live="polite"><strong>הצד השני מנסה להתחבר מחדש…</strong><span>כדאי להישאר כאן. אנחנו מנסים להחזיר את הצד השני לשיחה.</span></aside>}
      {currentUser && <FloatingError message={callError} onDismiss={() => setCallError('')} />}
      {currentUser && <NotificationCenter key={currentUser.id} socket={socket} userId={currentUser.id} sessionId={sessionId} planningPartnerId={currentStage === 8 ? partnerId : null}
        busy={(currentStage >= 2 && currentStage <= 6) || currentStage === 8 || showMatchRoomCall}
        onOpenChat={notice => {
          openNotificationChat(notice.partner_id, notice.partner_username);
        }}
        onAcceptCall={call => {
          setActiveChatMatch({ partner_id: call.from_user_id, partner_username: call.from_username });
          openNotificationChat(call.from_user_id, call.from_username);
          startMatchRoomCall(call.from_user_id, call.call_type);
        }} />}
      <div className="stars" style={{'--lobby-background-image': `url("${process.env.PUBLIC_URL}/backgrounds/lobby-sunset.jpeg")`, '--queue-background-image': `url("${process.env.PUBLIC_URL}/backgrounds/queue-sunset.jpeg")`}}>
        {currentUser && <SignOut />}
        {currentUser && <h1 className="app-head"><img className="app-brand-logo" src={`${process.env.PUBLIC_URL}/connext_logo.png`} alt="Connext" /></h1>}
        {currentUser ? (
          <div className="user-dashboard">
            {currentStage===1&&<PushNotifications userId={currentUser.id}/>}
            {(currentStage < 2 || currentStage === 7) && <Profile updateProfile={updateProfile} currentUser={currentUser} />}
            <div ref={videoStageRef} className={`main-stage ${(currentStage >= 3 && currentStage <= 6) || currentStage === 8 ? 'call-experience' : ''} ${currentStage === 5 || currentStage === 6 ? `is-video-stage ${panelsHidden?'video-panels-hidden':''}` : ''}`}>
              {(currentStage===5||currentStage===6)&&<span className="session-timer call-menu-timer" aria-label="הזמן שנותר בשלב הווידאו">{currentStage===5?formatTime(videoCallTimeLeft):"נחשפתם"}</span>}
              {(currentStage===5||currentStage===6)&&<details ref={callMenuRef} className="video-view-controls" onToggle={e=>{if(!e.currentTarget.open){setIsVideoPanelMinimized(true);}}} onKeyDown={e=>{if(e.key==='Escape'){e.currentTarget.open=false;e.currentTarget.querySelector('summary').focus();}}}><summary aria-label="תפריט שיחה">☰</summary><div className="video-menu-content">
                <button onClick={()=>{setPanelsHidden(v=>!v);setIsVideoPanelMinimized(true);}} aria-expanded={!panelsHidden}>{panelsHidden?"הצגת פרטי השיחה":"הסתרת פרטי השיחה"}</button>
                <button onClick={async()=>{try{const target=videoStageRef.current?.closest('.App');if(document.fullscreenElement)await document.exitFullscreen();else if(target?.requestFullscreen)await target.requestFullscreen();else setCallError("מסך מלא אינו נתמך כאן. הווידאו כבר ממלא את חלון האפליקציה.");}catch{setCallError("לא ניתן לפתוח מסך מלא. עדיין אפשר לצמצם את הלוחות.");}}}>{fullscreen?"יציאה ממסך מלא":"מסך מלא"}</button>
                {renderCallDock(currentStage===5?<button className="appearance-picker-trigger" onClick={()=>setIsVideoPanelMinimized(v=>!v)} aria-expanded={!isVideoPanelMinimized}>פילטרים</button>:null)}
              </div></details>}
              {((currentStage >= 3 && currentStage <= 6) || currentStage === 8) && <StageProgress stage={currentStage} />}
              {renderCurrentStage()}
          {currentStage === 5 && !isVideoPanelMinimized && <AppearanceDialog title="פילטרים לפנים" onClose={()=>setIsVideoPanelMinimized(true)}>
            <FilterPicker key={filterLibraryVersion} value={filterType} onChange={id=>{track('filter_selected');setFilterType(id);setIsVideoPanelMinimized(true);if(callMenuRef.current){callMenuRef.current.open=false;callMenuRef.current.querySelector('summary').focus();}}} />
          </AppearanceDialog>}
              <StageTransition stage={currentStage} />
            </div>
            {isChatVisible && <Chat socket={socket} currentUser={currentUser} closeChat={() => setIsChatVisible(false)} partnerId={partnerId} />}

            {/* Matches page overlay */}
            {showMatchesPage && !activeChatMatch && (
              <MatchesPage
                currentUser={currentUser}
                token={token || localStorage.getItem('token')}
                socket={socket}
                onClose={() => setShowMatchesPage(false)}
                onOpenChat={(match) => setActiveChatMatch(match)}
              />
            )}

            {/* Match chat room overlay */}
            {activeChatMatch && !showMatchRoomCall && (
              <MatchChatRoom
                currentUser={currentUser}
                token={token || localStorage.getItem('token')}
                match={activeChatMatch}
                socket={socket}
                onClose={() => setActiveChatMatch(null)}
                onStartVideoCall={startMatchRoomVideoCall}
                onStartPhoneCall={startMatchRoomPhoneCall}
              />
            )}

            {/* Match room active call overlay. Video calls reuse the post-reveal layout. */}
            {showMatchRoomCall && (
              <div className={`match-room-call-overlay ${matchRoomCallType === 'video' ? 'case-six is-video-stage' : ''}`}>
                <div className={matchRoomCallType === 'video' ? 'filters-off-container' : 'match-room-call-container'}>
                  {matchRoomCallType === 'video' ? (
                    <>
                      <MatchCallControls video muted={micMuted} onMute={toggleMute}
                        volume={remoteVolume} onVolume={setRemoteVolume} onEnd={endMatchRoomCall}
                        onReport={()=>setShowReportModal(true)} filter={filterType} filtersEnabled={matchFiltersEnabled}
                        onFilter={changeMatchFilter} onDisableFilters={disableMatchFilters} backgrounds={renderBackgrounds()}>{renderVoiceControls()}</MatchCallControls>

                      <div className="video-grid">
                        <div className="video-wrapper">
                          <video ref={matchRoomLocalVideoRef} autoPlay playsInline muted className="video-con local-video" />
                          <div className="video-label">אתם</div>
                        </div>
                        <div className="video-wrapper">
                          <video ref={matchRoomRemoteVideoRef} autoPlay playsInline className="video-con" />
                          <div className="video-label">{activeChatMatch?.partner_username || "הצד השני"}</div>
                        </div>
                      </div>


                    </>
                  ) : (
                    <>
                      <MatchCallControls onEnd={endMatchRoomCall}>{renderVoiceControls()}</MatchCallControls>

                      <div className="match-room-phone-ui">
                        <div className="match-room-phone-avatar">
                          {activeChatMatch?.partner_username?.[0]?.toUpperCase() || '?'}
                        </div>
                        <p className="match-room-phone-name">{activeChatMatch?.partner_username}</p>
                        <p className="match-room-phone-status">📞 שיחה פעילה…</p>
                        <audio ref={matchRoomRemoteVideoRef} autoPlay />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          renderAuthScreen()
        )}

        {/* Contact Us modal — rendered outside the condition so it works on both auth and lobby */}
        {showContactUs && (
          <ContactUs onClose={() => setShowContactUs(false)} />
        )}

        {/* Report Modal */}
        {showReportModal && (
          <ReportModal
            partnerId={showMatchRoomCall ? matchRoomPartnerIdRef.current : partnerId}
            partnerUsername={showMatchRoomCall ? activeChatMatch?.partner_username : partnerUsername}
            token={token || localStorage.getItem('token')}
            onClose={() => setShowReportModal(false)}
          />
        )}

      </div>
    </div>
    </PremiumContext.Provider>
  );
}

export default App;
